import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { busyAria } from "./a11y";
import type { UiTheme } from "./theme";

// THE "there is more, ask for it" control (PG-37).
//
// A paginated list that stops with no visible end is the defect this exists
// to prevent: before it, Measurements simply ended at the server's first
// page and said nothing, and the only other paginating surface (the feedback
// inbox) had rolled its own chip in local styles. Two call sites, one
// concept — so it is a primitive, per §CONSOLIDAMENTO, not a second copy.
//
// a11y floor (not decoration):
//  - 44pt minimum target, which the inbox's 44 already respected and the
//    measurement history would not have.
//  - `busy` goes through `busyAria`, NOT through `accessibilityState`:
//    react-native-web does not forward `accessibilityState` at all, so a
//    hand-rolled one is inert on exactly the surface this ships to
//    (RW-38, and `a11y.ts` says so in full). `disabled` carries itself —
//    RNW's Pressable emits `aria-disabled` from it.
//  - `aria-busy` is only the machine-readable half; no screen reader
//    announces it spontaneously. The label therefore sits in a polite live
//    region, so a reader that has already moved past the control still
//    hears the state change (WCAG 4.1.3).
//  - the state never rides on colour alone — the word changes too.

export type LoadMoreRowProps = {
  theme: UiTheme;
  /** False → renders nothing at all: no dead control, no empty gap. */
  hasMore: boolean;
  /** A page is in flight; the control stays visible but refuses re-entry. */
  loading?: boolean;
  /** Idle wording; defaults to the generic "Load more". */
  label?: string;
  onPress: () => void;
};

export function LoadMoreRow({ theme, hasMore, loading = false, label: idleLabel, onPress }: LoadMoreRowProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  if (!hasMore) return null;
  const label = loading
    ? t("common.loadingMore", { defaultValue: "Loading…" })
    : (idleLabel ?? t("common.loadMore", { defaultValue: "Load more" }));
  return (
    <View style={styles.wrap}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        {...busyAria(loading)}
        disabled={loading}
        onPress={onPress}
        style={({ pressed }) => [styles.btn, pressed && styles.btnPressed, loading && styles.btnBusy]}
      >
        <Text accessibilityLiveRegion="polite" style={styles.label}>
          {label}
        </Text>
      </Pressable>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { alignItems: "center", marginTop: s.s3 },
    btn: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r2,
    },
    btnPressed: { backgroundColor: p.surface, opacity: 0.85 },
    btnBusy: { opacity: 0.6 },
    label: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
  });
}
